import { useLanguage } from '@/hooks/useLanguage';

interface VibeFilterProps {
  vibes: string[];
  activeVibes: Set<string>;
  onToggle: (vibe: string) => void;
}

const VIBE_EMOJIS: Record<string, string> = {
  hungry: '🍜',
  adventurous: '🧭',
  'must-sees': '📸',
  'feeling-lucky': '🎲',
  popular: '🔥',
  'hidden-gems': '💎',
  '__custom__': '✨'
};

const SHORT_LABELS: Record<string, Record<string, string>> = {
  en: {
    adventurous: 'Adventure',
    'feeling-lucky': 'Interested',
    popular: 'Tourist',
    'hidden-gems': 'Spy',
  },
  fr: {
    adventurous: 'Aventure',
    'feeling-lucky': 'Cherche',
    popular: 'Touriste',
    'hidden-gems': 'Espion',
  },
  de: {
    adventurous: 'Abenteuer',
    'feeling-lucky': 'Interesse',
    popular: 'Tourist',
    'hidden-gems': 'Spion',
  },
  es: {
    adventurous: 'Aventura',
    'feeling-lucky': 'Interés',
    popular: 'Turista',
    'hidden-gems': 'Espía',
  },
};

export function VibeFilter({
  vibes,
  activeVibes,
  onToggle
}: VibeFilterProps) {
  const { t, language } = useLanguage();
  const useShortLabels = vibes.length >= 5;

  const getVibeLabel = (vibe: string) => {
    if (vibe.startsWith('custom:')) return vibe.slice(7);
    const fullLabel = t.vibes[vibe as keyof typeof t.vibes] || vibe;
    if (useShortLabels) {
      const shortLabel = SHORT_LABELS[language]?.[vibe] || SHORT_LABELS['en']?.[vibe];
      if (shortLabel) return shortLabel;
    }
    return fullLabel;
  };

  return (
    <div className="flex items-center justify-center gap-2 flex-wrap">
      {vibes.map(vibe => {
        const isActive = activeVibes.has(vibe);
        const emoji = vibe.startsWith('custom:') ? '✨' : (VIBE_EMOJIS[vibe] || '');

        return (
          <button
            key={vibe}
            onClick={() => onToggle(vibe)}
            className={`
              px-3 py-1.5 rounded-full text-sm font-medium transition-all
              flex items-center gap-1.5
              ${isActive
                ? 'bg-[var(--accent-teal)] text-white shadow-sm'
                : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]/80 border border-[var(--border)]'
              }
            `}
          >
            <span>{emoji}</span>
            <span>{getVibeLabel(vibe)}</span>
          </button>
        );
      })}
    </div>
  );
}
